import { useCallback, useEffect, useRef, useState } from "react";
import { serverNow } from "../utils/serverTime.js";
import { CLOSING_WARNING_MINUTES, getStoreStatus, isClosingSoon, resolveTimeZone } from "../utils/storeHours.js";

const TICK_MS = 30000;

const sameTime = (a, b) => (a?.getTime() ?? null) === (b?.getTime() ?? null);

const sameStatus = (a, b) =>
  a.open === b.open &&
  a.reason === b.reason &&
  a.closingSoon === b.closingSoon &&
  sameTime(a.closesAt, b.closesAt) &&
  sameTime(a.opensAt, b.opensAt);

/**
 * SOK-37: si el local está abierto según `store.schedule` y `store.closing_at`.
 *
 * Se reevalúa cada 30 segundos y además justo en el próximo cambio conocido (inicio del aviso,
 * cierre o apertura), así que el tótem cierra a la hora exacta y no hasta 30 s tarde. La tienda
 * llega por onSnapshot: si sok-admin mueve o borra `closing_at`, el cambio se ve de inmediato.
 * Usa la hora del servidor (useNow mantiene el desfase sincronizado).
 *
 * Solo dispara un re-render cuando el estado cambia; la cuenta regresiva lleva su propio reloj.
 * `onChange(next, previous)` avisa cuando el local abre o cierra, no en la primera evaluación.
 *
 * @param {Object|null} store - Documento de la tienda del tótem.
 * @param {Object|null} client - Documento del cliente (zona horaria de respaldo).
 * @param {Function} [onChange] - Se llama al pasar de abierto a cerrado o al revés.
 * @returns {{open: boolean, reason: string|null, closesAt: Date|null, opensAt: Date|null,
 *   closingSoon: boolean, timeZone: string}}
 */
export function useStoreHours(store, client, onChange) {
  const timeZone = resolveTimeZone(store, client);
  const schedule = store?.schedule;
  const closingAt = store?.closing_at;

  const evaluate = useCallback(() => {
    const now = serverNow();
    const status = getStoreStatus({ schedule, closing_at: closingAt }, now, timeZone);
    return { ...status, closingSoon: isClosingSoon(status, now), timeZone };
  }, [schedule, closingAt, timeZone]);

  const [status, setStatus] = useState(evaluate);
  // onChange cambia en cada render del padre; se lee desde una ref para no reiniciar el reloj.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastRef = useRef(null);

  useEffect(() => {
    let timer = null;
    const run = () => {
      const next = evaluate();
      setStatus((previous) => (sameStatus(previous, next) ? previous : next));
      // Sin tienda todavía (datos cargando) no hay nada que reportar.
      if (store) {
        const previous = lastRef.current;
        lastRef.current = next;
        if (previous && (previous.open !== next.open || previous.reason !== next.reason)) onChangeRef.current?.(next, previous);
      }

      const nowMs = serverNow().getTime();
      const changes = [next.closesAt, next.opensAt]
        .filter(Boolean)
        .map((date) => date.getTime());
      if (next.closesAt && !next.closingSoon) changes.push(next.closesAt.getTime() - CLOSING_WARNING_MINUTES * 60000);
      const delay = changes
        .map((at) => at - nowMs)
        .filter((ms) => ms > 0)
        .reduce((min, ms) => Math.min(min, ms), TICK_MS);
      // Un margen para no evaluar un instante antes del cambio.
      timer = setTimeout(run, delay + 50);
    };
    run();
    return () => clearTimeout(timer);
  }, [evaluate, store]);

  return status;
}
